import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import MenuSection from './MenuSection';
import { API_ENDPOINTS } from '../../config/api';

const FullscreenMenu = () => {
    const navigate = useNavigate();
    const [menuItems, setMenuItems] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchMenuItems = async () => {
            try {
                const restaurantData = localStorage.getItem('restaurantData');
                const restaurantId = restaurantData ? JSON.parse(restaurantData)._id : null;

                if (!restaurantId) {
                    console.error('Restaurant ID not found');
                    setLoading(false);
                    return;
                }

                const response = await axios.get(`${API_ENDPOINTS.MENU}?restaurantId=${restaurantId}`);
                setMenuItems(response.data);
            } catch (err) {
                console.error('Failed to load menu items:', err);
            } finally {
                setLoading(false);
            }
        };
        fetchMenuItems();
    }, []);

    const addMenuItem = async (newItem) => {
        try {
            const response = await axios.post(API_ENDPOINTS.MENU_ADD, newItem);
            setMenuItems([...menuItems, response.data]);
        } catch (err) {
            console.error('Failed to add menu item:', err);
        }
    };

    const updateMenuItem = async (id, updatedItem) => {
        try {
            const response = await axios.put(API_ENDPOINTS.MENU_ITEM(id), updatedItem);
            setMenuItems(menuItems.map(item => item._id === id ? response.data : item));
        } catch (err) {
            console.error('Failed to update menu item:', err);
        }
    };

    const deleteMenuItem = async (id) => {
        try {
            await axios.delete(API_ENDPOINTS.MENU_ITEM(id));
            setMenuItems(menuItems.filter(item => item._id !== id));
        } catch (err) {
            console.error('Failed to delete menu item:', err);
        }
    };

    const toggleAvailability = async (id) => {
        try {
            const response = await axios.put(API_ENDPOINTS.MENU_TOGGLE_AVAILABILITY(id));
            setMenuItems(menuItems.map(item => item._id === id ? response.data : item));
        } catch (err) {
            console.error('Failed to toggle availability:', err);
        }
    };

    const handleToggelQuickServe = async (id) => {
        try {
            const response = await axios.put(API_ENDPOINTS.MENU_TOGGLE_QUICK_SERVE(id));
            setMenuItems(menuItems.map(item => item._id === id ? response.data : item));
        } catch (err) {
            console.error('Failed to toggle quick serve:', err);
        }
    };

    return (
        <div className="min-h-screen w-full bg-gradient-to-br from-amber-50/50 via-white to-orange-50/40">
            {/* Header — full width, small back button */}
            <header className="sticky top-0 z-20 bg-white/95 backdrop-blur-sm border-b-2 border-orange-400 shadow-sm">
                <div className="w-full px-2 sm:px-4 py-3">
                    <div className="flex items-center justify-between gap-4">
                        <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                            <button
                                onClick={() => navigate(-1)}
                                className="flex-shrink-0 p-1.5 rounded-lg bg-stone-100 text-stone-700 hover:bg-stone-200 active:scale-95 transition-all"
                                aria-label="Back"
                            >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                                </svg>
                            </button>
                            <div>
                                <h1 className="text-xl sm:text-2xl font-bold text-stone-800 truncate">
                                    Menu Management
                                </h1>
                                <p className="text-xs sm:text-sm text-stone-500 mt-0.5">
                                    Fullscreen view · Manage your menu items
                                </p>
                            </div>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                            <span className="hidden sm:inline text-sm font-medium text-stone-500 bg-stone-100 px-3 py-1.5 rounded-lg">
                                {menuItems.length} items
                            </span>
                        </div>
                    </div>
                </div>
            </header>

            {/* Content — full width, minimal padding */}
            <main className="w-full px-2 sm:px-4 py-3 sm:py-5">
                {loading ? (
                    <div className="flex flex-col items-center justify-center py-24">
                        <div className="w-10 h-10 border-2 border-orange-400 border-t-transparent rounded-full animate-spin mb-4" />
                        <p className="text-stone-500 font-medium">Loading menu...</p>
                    </div>
                ) : (
                    <MenuSection
                        menuItems={menuItems}
                        allMenuItems={menuItems}
                        addMenuItem={addMenuItem}
                        updateMenuItem={updateMenuItem}
                        deleteMenuItem={deleteMenuItem}
                        toggleAvailability={toggleAvailability}
                        handleToggelQuickServe={handleToggelQuickServe}
                        isFullscreen={true}
                    />
                )}
            </main>
        </div>
    );
};

export default FullscreenMenu;
